/**
 * FormActions Component
 *
 * Consistent action button layout for forms, wizards, and edit modes.
 * Provides Cancel/Save Draft/Publish patterns across the application.
 *
 * Epic: MVP 1.27 - Experience Creation Wizard (Component Extraction)
 * Usage: Inline edit forms, wizard navigation, modal actions
 */

/**
 * FormActions Component
 *
 * @param {Function} onCancel - Handler for cancel/back action
 * @param {Function} onSaveDraft - Handler for save draft action (optional)
 * @param {Function} onSubmit - Handler for primary submit action
 * @param {String} cancelLabel - Label for cancel button (default: "Cancel")
 * @param {String} saveDraftLabel - Label for save draft button (default: "Save Draft")
 * @param {String} submitLabel - Label for submit button (default: "Save")
 * @param {Boolean} isSaving - Loading state for submit action
 * @param {Boolean} isSavingDraft - Loading state for draft action
 * @param {Boolean} hasUnsavedChanges - Enable/disable save buttons
 * @param {Boolean} showSaveDraft - Show the save draft button (default: true if onSaveDraft provided)
 * @param {Boolean} disableCancel - Disable cancel button
 * @param {Boolean} disableSubmit - Disable submit button
 * @param {String} submitVariant - Visual style: 'primary' | 'success' | 'danger' (default: 'success')
 * @param {String} layout - Layout style: 'split' | 'right' (default: 'split')
 * @param {String} className - Additional CSS classes
 */
export default function FormActions({
  onCancel,
  onSaveDraft,
  onSubmit,
  cancelLabel = 'Cancel',
  saveDraftLabel = 'Save Draft',
  submitLabel = 'Save',
  isSaving = false,
  isSavingDraft = false,
  hasUnsavedChanges = true,
  showSaveDraft,
  disableCancel = false,
  disableSubmit = false,
  submitVariant = 'success',
  layout = 'split',
  className = '',
  categoryL1Id: _categoryL1Id,
  ...props
}) {
  // Determine if save draft button should be shown
  const shouldShowSaveDraft = showSaveDraft ?? (onSaveDraft !== undefined);

  // Compute button states (hoist boolean logic)
  const canSave = hasUnsavedChanges && !isSaving && !isSavingDraft && !disableSubmit;
  const canSaveDraft = hasUnsavedChanges && !isSaving && !isSavingDraft && shouldShowSaveDraft;

  // Submit button variants
  const submitVariants = {
    primary: 'bg-blue-600 hover:bg-blue-700 text-white',
    success: 'bg-emerald-600 hover:bg-emerald-700 text-white',
    danger: 'bg-red-600 hover:bg-red-700 text-white'
  };

  const submitButtonClasses = submitVariants[submitVariant] || submitVariants.success;

  // Layout styles
  const containerClasses = layout === 'right'
    ? 'flex items-center justify-end gap-3'
    : 'flex items-center justify-between';

  return (
    <div className={`mt-8 pt-6 border-t border-gray-200 ${containerClasses} ${className}`} {...props}>
      {/* Cancel/Back Button (left side in split layout) */}
      <button
        type="button"
        onClick={onCancel}
        disabled={disableCancel || isSaving || isSavingDraft}
        className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        aria-label={cancelLabel}
      >
        {cancelLabel}
      </button>

      {/* Action Buttons (right side) */}
      <div className="flex items-center gap-3">
        {/* Save Draft Button (optional middle button) */}
        {shouldShowSaveDraft && (
          <button
            type="button"
            onClick={onSaveDraft}
            disabled={!canSaveDraft}
            className="px-6 py-2 text-gray-700 bg-yellow-100 hover:bg-yellow-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-2"
            aria-label={saveDraftLabel}
          >
            {isSavingDraft && <LoadingSpinner size="sm" />}
            {isSavingDraft ? 'Saving...' : saveDraftLabel}
          </button>
        )}

        {/* Primary Submit Button (right button) */}
        <button
          type="submit"
          onClick={onSubmit}
          disabled={!canSave}
          className={`px-6 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold flex items-center gap-2 ${submitButtonClasses}`}
          aria-label={submitLabel}
        >
          {isSaving && <LoadingSpinner size="sm" />}
          {isSaving ? 'Saving...' : submitLabel}
        </button>
      </div>
    </div>
  );
}

/**
 * Loading Spinner Component
 */
function LoadingSpinner({ size = 'md' }) {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5'
  };

  return (
    <svg
      className={`animate-spin ${sizeClasses[size]}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
      />
    </svg>
  );
}

/**
 * Preset configurations for common use cases
 */
export const FormActionsPresets = {
  // Inline edit mode (Cancel | Save Draft | Save & Publish)
  inlineEdit: {
    cancelLabel: 'Cancel',
    saveDraftLabel: 'Save Draft',
    submitLabel: 'Save & Publish',
    showSaveDraft: true,
    submitVariant: 'success',
    layout: 'split'
  },

  // Wizard navigation (Back | Continue)
  wizardNavigation: {
    cancelLabel: 'Back',
    submitLabel: 'Continue',
    showSaveDraft: false,
    submitVariant: 'success',
    layout: 'split'
  },

  // Wizard final stage (Back | Publish Experience)
  wizardPublish: {
    cancelLabel: 'Back',
    submitLabel: 'Publish Experience',
    showSaveDraft: false,
    submitVariant: 'success',
    layout: 'split'
  },

  // Modal actions (Cancel | Confirm)
  modalActions: {
    cancelLabel: 'Cancel',
    submitLabel: 'Confirm',
    showSaveDraft: false,
    submitVariant: 'primary',
    layout: 'right'
  },

  // Danger actions (Cancel | Delete)
  dangerActions: {
    cancelLabel: 'Cancel',
    submitLabel: 'Delete',
    showSaveDraft: false,
    submitVariant: 'danger',
    layout: 'right'
  }
};
